import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { managementService } from '../../service/management.service';

interface IProps {
  children: React.ReactNode;
  os?: string;
  label: string;
  /** Product slug for multi-product downloads. Omit for the default (TouchPose). */
  product?: string;
  /**
   * Custom download handler (e.g. gated downloads that must inspect the HTTP
   * status). Replaces the default os/product plugin URL; the handler owns its
   * own toasts, the button only tracks the busy state and toasts failures on
   * a thrown error.
   */
  onDownload?: () => Promise<void>;
}

export const ButtonContainer = ({
  children,
  os,
  label,
  product,
  onDownload
}: IProps) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      if (onDownload) {
        await onDownload();
      } else {
        const url = managementService.getFile(os ?? '', product);
        const a = document.createElement('a');
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(t('download.started'));
      }
    } catch {
      toast.error(t('download.failed'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isDownloading}
      className={`flex-1 min-w-[160px] text-left text-content-dark ${isDownloading ? 'cursor-wait' : 'cursor-pointer'}`}
    >
      <div
        className={`flex items-center gap-[10px] bg-surface-section border border-border-dark/[0.11] rounded-[11px] px-[15px] py-[12px] transition-all group ${isDownloading ? 'opacity-60' : 'hover:border-blue hover:bg-surface-hover hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(14,165,201,0.12)]'}`}
      >
        <div className="w-[34px] h-[34px] rounded-[8px] border border-border-dark/[0.07] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] flex items-center justify-center shrink-0 text-[16px]">
          {children}
        </div>
        <span className="flex-1 font-body text-[13.5px] font-medium">
          {isDownloading ? t('download.inProgress') : label}
        </span>
        {isDownloading ? (
          <svg
            className="animate-spin text-blue shrink-0"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <circle
              cx="7"
              cy="7"
              r="5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="10 6"
            />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="text-content-muted-light shrink-0 transition-all group-hover:translate-y-[2px] group-hover:text-brand"
          >
            <path
              d="M7 1.75v7.5M3.5 5.75L7 9.25l3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </button>
  );
};
